// =============================================================================
// Auth API
// =============================================================================
import { apiClient, setStoredTokens, clearStoredTokens } from './http';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  // ⚠️ NO hay `user` aquí. El backend devuelve `Token` (access, refresh, tipo
  // y caducidad) y nada más; este campo lo declaraba el cliente y no lo manda
  // nadie. Nadie lo leía —el usuario se obtiene con `GET /auth/me`— pero el
  // primero que se fiara del tipo recibiría `undefined` en tiempo de
  // ejecución con TypeScript diciéndole que existe.
}

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  // OAuth2 password flow: el backend espera form-data username/password,
  // NO JSON. Con JSON el login devuelve 422 y parece "contraseña mal".
  const form = new URLSearchParams({
    username: credentials.email,
    password: credentials.password,
  });
  // OJO: el cliente pone Content-Type: application/json por defecto, y eso
  // hace que axios serialice el URLSearchParams como JSON vacío → 422. Hay
  // que forzar la cabecera de formulario AQUÍ, no basta con pasar el form.
  const response = await apiClient.post<LoginResponse>('/auth/login', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  // Store tokens
  setStoredTokens({
    accessToken: response.data.access_token,
    refreshToken: response.data.refresh_token,
  });

  return response.data;
}

export async function logout(): Promise<void> {
  // No hay endpoint de logout: los JWT son sin estado y no se pueden
  // invalidar server-side. Cerrar sesión = olvidar los tokens.
  clearStoredTokens();
}

export interface RecuperarAccesoResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

/**
 * Recuperar el acceso sin correo: el QR del centro (dinámico, caduca a los
 * 30 s) prueba dónde y de qué empresa; el PIN, quién. Hermano de `login`
 * —mismo par de tokens, se guardan igual— pero sin usuario ni contraseña:
 * el trabajador nunca escribe un email.
 */
export async function recuperarAcceso(
  worksiteUuid: string,
  qrToken: string,
  pin: string,
): Promise<RecuperarAccesoResponse> {
  const response = await apiClient.post<RecuperarAccesoResponse>('/auth/recuperar-acceso', {
    worksite_uuid: worksiteUuid,
    qr_token: qrToken,
    pin,
  });

  setStoredTokens({
    accessToken: response.data.access_token,
    refreshToken: response.data.refresh_token,
  });

  return response.data;
}
